import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import {
  CheckCircle2,
  AlertTriangle,
  Info,
  XCircle,
} from "lucide-react"

const alertVariants = cva(
  "relative w-full rounded-xl px-4 py-3.5 text-sm",
  {
    variants: {
      variant: {
        default:
          "bg-muted/60 text-foreground [&_.alert-icon]:text-muted-foreground",
        destructive:
          "bg-red-50 text-red-900 dark:bg-red-950/40 dark:text-red-200 [&_.alert-icon]:text-red-600 dark:[&_.alert-icon]:text-red-400",
        success:
          "bg-green-50 text-green-900 dark:bg-green-950/40 dark:text-green-200 [&_.alert-icon]:text-green-600 dark:[&_.alert-icon]:text-green-400",
        warning:
          "bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200 [&_.alert-icon]:text-amber-600 dark:[&_.alert-icon]:text-amber-400",
        info:
          "bg-blue-50 text-blue-900 dark:bg-blue-950/40 dark:text-blue-200 [&_.alert-icon]:text-blue-600 dark:[&_.alert-icon]:text-blue-400",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

type AlertVariant = NonNullable<VariantProps<typeof alertVariants>["variant"]>

const defaultIcons: Record<AlertVariant, React.ElementType> = {
  default: Info,
  destructive: XCircle,
  success: CheckCircle2,
  warning: AlertTriangle,
  info: Info,
}

interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {
  icon?: React.ElementType | null
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant = "default", icon, children, ...props }, ref) => {
    const resolvedVariant = variant ?? "default"
    const IconComponent = icon === null ? null : icon ?? defaultIcons[resolvedVariant]

    return (
      <div
        ref={ref}
        role="alert"
        className={cn(alertVariants({ variant: resolvedVariant }), className)}
        {...props}
      >
        <div className="flex items-start gap-3">
          {IconComponent && (
            <IconComponent className="alert-icon mt-0.5 h-4 w-4 shrink-0" />
          )}
          <div className="min-w-0 flex-1">{children}</div>
        </div>
      </div>
    )
  }
)
Alert.displayName = "Alert"

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn("font-semibold leading-snug", className)}
    {...props}
  />
))
AlertTitle.displayName = "AlertTitle"

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm leading-relaxed [&_p]:leading-relaxed", className)}
    {...props}
  />
))
AlertDescription.displayName = "AlertDescription"

export { Alert, AlertTitle, AlertDescription }
